/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

import { invalidPlaceholderStyle } from "@/utils/placeholderValidation";
import { decoratePlaceholderTokens, placeholderPillStyle } from "@/utils/placeholderPills";
import { EMAIL_SAFE_VALID_ELEMENTS } from "@/utils/tinymceEditor.constants";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { getSuggestions } from "@/pages/Private/pages/List/utils/column-utils";
import { getSuggestionLabel } from "@/pages/Private/pages/List/components/CampaignOverview/utils/suggestions.utils";

export const PromptEditor: FunctionComponent<{
	handleChange: (value: string) => void;
	handleInputChange?: (value: boolean) => void;
	currentLeadIndex?: number;
	maxLength?: number;
	placeholder?: string;
	value?: string;
}> = ({ handleChange, handleInputChange, currentLeadIndex = 0, maxLength, placeholder, value }) => {
	const { inbox } = useAppSelector(listSelector);

	const editorRef = useRef<any>(null);
	const statusNodeRef = useRef<HTMLSpanElement | null>(null);

	const currentPerson = useMemo(() => {
		return inbox[currentLeadIndex];
	}, [currentLeadIndex, inbox]);

	const suggestions = useMemo(() => {
		return inbox[currentLeadIndex]
			? getSuggestions(inbox[currentLeadIndex], true)
			: getSuggestions();
	}, [currentLeadIndex, inbox]);

	const processedSuggestions = useMemo(() => {
		return suggestions
			.map((key) => ({
				text: getSuggestionLabel(key, currentPerson),
				value: key,
			}))
			.filter((item) => item.text !== undefined);
	}, [suggestions, currentPerson]);

	return processedSuggestions ? (
		<div className="relative flex flex-col gap-2">
			<Editor
				ref={editorRef}
				apiKey={import.meta.env.VITE_TINY_MCE} // optional for cloud features
				init={{
					placeholder: placeholder,
					valid_classes: "mceNonEditable",
					menubar: false,
					height: 360,
					branding: false,
					elementpath: false,
					extended_valid_elements:
						"span[data-placeholder|data-invalid|data-pill-type|title|data-mce-noneditable|class]",
					toolbar: "bold italic underline removeformat",

					block_formats: "Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3",
					content_style: `
					body { font-family: Arial, Helvetica, sans-serif; font-size:14px; }

					${placeholderPillStyle}
					${invalidPlaceholderStyle}

          a { text-decoration: underline; }
          table, td { border-collapse: collapse; }

				  `,
					toolbar_mode: "wrap",
					noneditable_noneditable_class: "mceNonEditable",
					plugins: "link lists paste code visualchars wordcount noneditable",
					font_family_formats: `
      Arial=arial,helvetica,sans-serif;
      Georgia=georgia,serif;
	  Helvetica=helvetica,sans-serif;
      Times New Roman='Times New Roman',times,serif;
      Tahoma=tahoma,verdana,sans-serif;
      Trebuchet MS='Trebuchet MS',helvetica,sans-serif;
      Verdana=verdana,geneva,sans-serif;
      Courier New='Courier New',courier,monospace
    `,
					// Keep the HTML simple & email-safe:
					forced_root_block: "p", // avoid <div>
					valid_children: "+body[p]",
					// Only allow common email-safe tags/attrs (no <img> — see constant).
					valid_elements: EMAIL_SAFE_VALID_ELEMENTS,
					// URLs in email should be absolute:
					convert_urls: false,
					relative_urls: false,
					remove_script_host: false,
					paste_as_text: false,
					// Never inline pasted images as base64 (bloats campaignConfig → 413).
					paste_data_images: false,
					// Keep inline styles (fonts/colors) that email clients expect:
					invalid_styles: { "*": "margin" }, // strip margins (often ignored in email)
					uploadcare_public_key: "fcdffd80684e1dd9782f",
					content_css: "/custom-tinymce.css",

					setup: function (editor: any) {
						editor.on("BeforeSetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Turn {first_name} into a pill span, **only** if not already wrapped
							e.content = decoratePlaceholderTokens(e.content, {
								processedSuggestions,
								columns: currentPerson?.list?.columns,
							});
						});

						// 2) When content is read out of the editor
						editor.on("GetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Replace spans back to {variable}
							e.content = e.content.replace(
								/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
								(match: string, varName: string) => `{${varName}}`
							);
						});
						editor.on("keyup", (e: KeyboardEvent) => {
							handleInputChange?.(true);
							const characterCount = editor.plugins.wordcount?.body?.getCharacterCount() ?? 0;
							const text = editor.getBody().innerText;

							if (
								maxLength &&
								characterCount >= maxLength &&
								![
									"Backspace",
									"Delete",
									"ArrowLeft",
									"ArrowRight",
									"ArrowUp",
									"ArrowDown",
								].includes(e.key)
							) {
								e.preventDefault(); // stop further input

								editor.getBody().innerText = text.slice(
									0,
									text.length - (characterCount - maxLength)
								);
							}
						});

						editor.ui.registry.addAutocompleter("variables", {
							trigger: "{", // ← use this (not `ch`)
							minChars: 0, // show immediately after `{`
							// default predicate requires start-of-word; allow anywhere:
							matches: () => true,
							fetch: (pattern: any) => {
								const list = processedSuggestions.filter((v) =>
									v.text.toLowerCase().includes(pattern.toLowerCase())
								);

								return Promise.resolve(
									list.map((v) => ({ type: "autocompleteitem", text: v.text, value: v.value }))
								);
							},
							onAction: (api: any, rng: any, value: any) => {
								editor.selection.setRng(rng);

								const text = processedSuggestions.find((v) => v.value === value);
								// If the next char is already '}', avoid duplicating it.
								const node = editor.selection.getNode();
								const contentAfter =
									node?.textContent?.slice(editor.selection.getRng().startOffset) ?? "";
								const needsClose = !contentAfter?.startsWith("}");

								editor.insertContent(
									needsClose ? "{" + value + "|" + (text?.text || value) + "}" : value
								);
								api.hide();
							},
						});

						const ensureStatusNode = () => {
							// Prefer the text container; fall back to full statusbar
							const container = editor.getContainer();
							const sb =
								container.querySelector(".tox-statusbar__text-container") ||
								container.querySelector(".tox-statusbar");

							if (sb && !statusNodeRef.current) {
								const el = document.createElement("span");

								el.className = "my-statusbar-left";
								el.textContent = "Type { to insert variables";
								// Put it at the start so it sits left of the element path/wordcount
								sb.prepend(el);
								statusNodeRef.current = el as HTMLSpanElement;
							}
						};

						// Run once the chrome is there; SkinLoaded covers theme load too
						editor.on("init SkinLoaded", ensureStatusNode);

						editor.on("remove", () => {
							statusNodeRef.current = null;
						});
					},
				}}
				initialValue={value}
				onBlur={(e: any) => {
					let text = e.target.getContent();
					const editor = e.target.editorCommands?.editor;

					const characterCount = editor.plugins.wordcount?.body?.getCharacterCount() ?? 0;

					if (maxLength) {
						text = text.slice(0, text.length - (characterCount - maxLength));

						e.target.getContent = () => text.slice(0, text.length - (characterCount - maxLength));
					}

					text = text.replace(
						/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
						(match: string, name: string) => `{${name}}`
					);

					handleChange(text);
				}}
				onInit={() => {
					(document.querySelector("button.tox-statusbar__wordcount") as HTMLElement)?.click();
				}}
			/>
		</div>
	) : (
		<></>
	);
};
